/**
 * Render Configuration
 *
 * Centralized configuration for ASCII rendering calculations.
 * Extracts magic numbers to make them more maintainable and configurable.
 */

export const RENDER_CONFIG = {
  // Font size calculation
  maxFontSize: 11,
  minFontSize: 4,
  baseCanvasWidth: 800,
  fontWidthRatio: 0.6,

  // Prompt input
  characterLimitWarning: 400,
  maxPromptLength: 500,

  // Video frame extraction
  videoFramesPerSecond: 10,
  maxVideoSeconds: 10,
  videoPlaybackFPS: 24,

  // Animation
  animationDuration: 800, // ms for randomize animation
  toastDuration: 2000, // ms for success/copied notifications
  downloadDelay: 400, // ms delay before download to show animation

  // GIF export
  gifEstimatedFrames: 30,
  gifCompressionFactor: 10,

  // PNG export
  pngCompressionFactor: 5,
  pngExportScale: 3 // Render scale multiplier for higher quality exports
};

/**
 * Calculate font size based on columns
 * @param {number} columns - Number of columns
 * @returns {number} - Font size in pixels
 */
export const calculateFontSize = (columns) => {
  return Math.max(
    RENDER_CONFIG.minFontSize,
    Math.min(RENDER_CONFIG.maxFontSize, RENDER_CONFIG.baseCanvasWidth / columns)
  );
};

/**
 * Estimate file size for exports
 * @param {string} format - 'png' or 'gif'
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @param {number} characterCount - Number of characters
 * @returns {string} - Formatted file size estimate
 */
export const estimateFileSize = (format, width, height, characterCount) => {
  if (format === 'gif') {
    // GIF: ~30 frames, each frame is roughly canvas size
    const estimatedBytes = (width * height * RENDER_CONFIG.gifEstimatedFrames * 4) / RENDER_CONFIG.gifCompressionFactor;
    const mb = estimatedBytes / (1024 * 1024);
    return mb > 1 ? `~${mb.toFixed(1)}MB` : `~${(estimatedBytes / 1024).toFixed(0)}KB`;
  } else {
    // PNG: Based on resolution and character count
    const estimatedBytes = (width * height * characterCount * 2) / RENDER_CONFIG.pngCompressionFactor;
    const kb = estimatedBytes / 1024;
    return kb > 1000 ? `~${(kb / 1024).toFixed(1)}MB` : `~${kb.toFixed(0)}KB`;
  }
};
